// partners/adminView/pages/A2_AdminApplicationsList.jsx
// Applications list with status/type filters and CSV export.

import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  useGetAdminApplicationsQuery,
  useExportApplicationsCSVQuery,
} from "../../../apis/adminApiSlice";
import AdminLayout from "./A0_AdminLayout";
import DeleteApplicationModal from "../DeleteApplicationModal";
import { applicationCanBeDeleted } from "../applicationDeleteUtils";
import { capturePartnerEvent } from "../../../utils/analytics";

const STATUS_BADGE = {
  pending:      "bg-yellow-500/20 text-yellow-400",
  under_review: "bg-mnf-cyan/20 text-mnf-cyan",
  approved:     "bg-mnf-success/20 text-mnf-success",
  rejected:     "bg-mnf-error/20 text-mnf-error",
  waitlisted:   "bg-mnf-purple/20 text-mnf-purple",
};

export default function AdminApplicationsList() {
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter,   setTypeFilter]   = useState("");
  const [doExport,     setDoExport]     = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isLoading } = useGetAdminApplicationsQuery({
    status: statusFilter,
    type:   typeFilter,
  });

  const { data: csvText, isFetching: csvFetching } = useExportApplicationsCSVQuery(
    { status: statusFilter, type: typeFilter },
    { skip: !doExport }
  );

  const applications = data?.results || data || [];
  const viewedRef = useRef(false);

  useEffect(() => {
    if (!isLoading && !viewedRef.current) {
      viewedRef.current = true;
      capturePartnerEvent("partner_admin_applications_viewed");
    }
  }, [isLoading]);

  useEffect(() => {
    if (!doExport || csvFetching || !csvText) return;
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mnf_partner_applications.csv";
    a.click();
    URL.revokeObjectURL(url);
    setDoExport(false);
  }, [doExport, csvText, csvFetching]);

  const handleCSV = () => {
    capturePartnerEvent("partner_admin_applications_exported", {
      status: statusFilter || undefined,
      type: typeFilter || undefined,
    });
    setDoExport(true);
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-poppins text-2xl font-bold">Applications</h1>
        <button
          onClick={handleCSV}
          className="text-xs font-poppins font-semibold px-4 py-2 rounded-mnf-pill border border-white/20 text-mnf-text-secondary hover:border-mnf-cyan/40 transition-colors"
        >
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <select
          value={statusFilter}
          onChange={(e) => {
            const val = e.target.value;
            setStatusFilter(val);
            capturePartnerEvent("partner_admin_applications_filtered", {
              status: val || undefined,
              type: typeFilter || undefined,
            });
          }}
          className="bg-mnf-dark-3 border border-white/[0.08] text-mnf-text-secondary rounded-mnf px-3 py-2 text-sm focus:outline-none focus:border-mnf-cyan"
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="under_review">Under Review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="waitlisted">Waitlisted</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => {
            const val = e.target.value;
            setTypeFilter(val);
            capturePartnerEvent("partner_admin_applications_filtered", {
              status: statusFilter || undefined,
              type: val || undefined,
            });
          }}
          className="bg-mnf-dark-3 border border-white/[0.08] text-mnf-text-secondary rounded-mnf px-3 py-2 text-sm focus:outline-none focus:border-mnf-cyan"
        >
          <option value="">All types</option>
          <option value="ecosystem">Film Ecosystem Partner</option>
          <option value="creative_ambassador">Creative Ambassador</option>
        </select>
      </div>

      {isLoading ? (
        <div className="text-mnf-text-muted text-sm">Loading...</div>
      ) : (
        <div className="bg-mnf-dark-2 border border-white/[0.08] rounded-mnf-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-mnf-dark-3 text-mnf-text-muted text-xs uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3 text-left">Name</th>
                <th className="px-5 py-3 text-left">Type</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-left">Score</th>
                <th className="px-5 py-3 text-left">Submitted</th>
                <th className="px-5 py-3 text-left"></th>
              </tr>
            </thead>
            <tbody>
              {applications.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-mnf-text-muted">
                    No applications found.
                  </td>
                </tr>
              )}
              {applications.map((app) => (
                <tr
                  key={app.id}
                  className="border-t border-white/[0.04] hover:bg-mnf-dark-3 transition-colors"
                >
                  <td className="px-5 py-3 font-semibold">{app.full_name}</td>
                  <td className="px-5 py-3 text-mnf-text-secondary text-xs">
                    {app.partner_type === "creative_ambassador" ? "Ambassador" : "Ecosystem"}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`text-xs font-poppins font-semibold px-2.5 py-1 rounded-full ${
                        STATUS_BADGE[app.status] || "bg-white/10 text-white"
                      }`}
                    >
                      {app.status?.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-mnf-text-muted">{app.review_score ?? "—"}</td>
                  <td className="px-5 py-3 text-mnf-text-muted text-xs">
                    {new Date(app.submitted_at).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      {applicationCanBeDeleted(app.status) && (
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(app)}
                          className="text-xs text-mnf-error hover:underline"
                        >
                          Delete
                        </button>
                      )}
                      <Link
                        to={`/partners/admin/applications/${app.id}`}
                        className="text-xs text-mnf-cyan hover:underline"
                      >
                        Review →
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteTarget && (
        <DeleteApplicationModal
          application={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => setDeleteTarget(null)}
        />
      )}
    </AdminLayout>
  );
}
